import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { useAppStore } from '../../../../store/appStore';
import type { StorageTransferSettings } from '../../../../types/azureStorageTransfers';

type SettingKey = keyof StorageTransferSettings;

const FIELDS: Array<{ key: SettingKey; label: string; min: number; max: number; help: string }> = [
  { key: 'maxConcurrentJobs', label: 'Concurrent transfer jobs', min: 1, max: 8, help: 'Maximum uploads and downloads that run at once.' },
  { key: 'blockConcurrency', label: 'Blocks per file', min: 1, max: 32, help: 'Maximum upload blocks staged concurrently for one large file.' },
  { key: 'blockSizeMiB', label: 'Block size (MiB)', min: 4, max: 100, help: 'Block size used for resumable large-file uploads.' },
];

export function AzureStorageSettingsSection() {
  const settings = useAppStore((state) => state.azureStorageTransferSettings);
  const setSettings = useAppStore((state) => state.setAzureStorageTransferSettings);
  const { track } = useAnalytics();

  const update = (key: SettingKey, value: string) => {
    const field = FIELDS.find((candidate) => candidate.key === key)!;
    const parsed = Number(value);
    const bounded = Math.min(field.max, Math.max(field.min, Number.isFinite(parsed) ? Math.round(parsed) : settings[key]));
    setSettings({ [key]: bounded });
    track('feature:storage_transfer_settings', { setting: key });
  };

  return (
    <div className="space-y-5" data-testid="azure-storage-settings-section">
      <p className="text-sm text-muted-foreground">Tune transfer concurrency and resumable upload blocks. Limits are enforced in both the app and storage process.</p>
      {FIELDS.map((field) => (
        <div key={field.key} className="grid gap-1.5">
          <label htmlFor={`azure-storage-${field.key}`} className="text-sm font-medium">{field.label}</label>
          <input
            id={`azure-storage-${field.key}`}
            type="number"
            min={field.min}
            max={field.max}
            value={settings[field.key]}
            onChange={(event) => update(field.key, event.target.value)}
            className="h-8 w-28 rounded-md border border-input bg-background px-2 text-sm tabular-nums"
          />
          <p className="text-xs text-muted-foreground">{field.help}</p>
        </div>
      ))}
    </div>
  );
}
